// ============================================================================
// FRAMEWORK REVIEWS - AUDITOR PAGE
// ============================================================================
// Purpose: Review compliance frameworks assigned to companies
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { auditApi, FrameworkReview } from '../../audit/api';
import { complianceExecutionApi } from '../../compliance/execution/api/complianceExecution.api';
import { useModal } from '../../../components/modal';
import { RefreshCw, FileText } from 'lucide-react';
import PageInfoButton from '@/components/PageInfoButton';

const FrameworkReviews: React.FC = () => {
  const navigate = useNavigate();
  const { openModal } = useModal();
  const [frameworks, setFrameworks] = useState<FrameworkReview[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFrameworkReviews();
  }, []);

  const loadFrameworkReviews = async () => {
    try {
      const frameworkData = await auditApi.getFrameworksForReview();
      setFrameworks(frameworkData);
    } catch (error) {
      console.error('Failed to load framework reviews:', error);
      setFrameworks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForExternalAudit = async (frameworkId: number) => {
    try {
      await complianceExecutionApi.submitForExternalAudit(frameworkId);
      toast.success('Framework submitted for external audit successfully!');
      await loadFrameworkReviews();
    } catch (error) {
      console.error('Error submitting for external audit:', error);
      toast.error('Failed to submit for external audit. Ensure all evidence is internally verified.');
    }
  };

  const handleStartReview = (_frameworkId: number) => {
    navigate('/audit/evidence');
  };

  const handleViewDetails = (framework: FrameworkReview) => {
    openModal({
      component: ({ onClose }: { onClose: () => void }) => (
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
              <FileText size={18} className="text-slate-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">Framework Details</h3>
              <p className="text-slate-500 text-sm">Review framework information</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1">Framework Name</label>
                <p className="text-sm font-semibold text-slate-900">{framework.framework_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1">Company</label>
                <p className="text-sm font-semibold text-slate-900">{framework.company_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1">Compliance Score</label>
                <p className="text-sm font-semibold text-slate-900">{framework.compliance_score}%</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1">Requirements</label>
                <p className="text-sm font-semibold text-slate-900">
                  {framework.completed_requirements}/{framework.total_requirements}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1">Critical Findings</label>
                <p className="text-sm font-semibold text-red-600">{framework.critical_findings}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1">Status</label>
                <span className={`inline-flex px-2 py-1 rounded-md font-bold text-[10px] ${
                  framework.status === 'completed' ? 'bg-green-100 text-green-800' :
                  framework.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {framework.status}
                </span>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-200">
              <label className="block text-sm font-medium text-slate-500 mb-1">Next Review Date</label>
              <p className="text-sm text-slate-900">{framework.next_review_date || 'Not scheduled'}</p>
            </div>

            <div className="flex justify-end pt-4">
              <button
                onClick={onClose}
                className="px-4 py-2.5 bg-slate-600 text-white font-medium text-sm rounded-lg hover:bg-slate-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ),
      options: {
        size: 'large'
      }
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Compliant': return 'text-green-700 bg-green-100';
      case 'In Progress': return 'text-yellow-700 bg-yellow-100';
      case 'Under Review': return 'text-blue-700 bg-blue-100';
      case 'Non-Compliant': return 'text-red-700 bg-red-100';
      default: return 'text-gray-700 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="text-blue-600 animate-spin" size={40} />
          <p className="text-slate-600 font-medium text-sm">Loading framework reviews...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Framework Reviews</h1><PageInfoButton /></div>
            <p className="text-slate-500 text-sm">Review compliance frameworks and assess implementation status</p>
          </div>
          <button
            onClick={loadFrameworkReviews}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        {/* Framework List */}
        <div className="space-y-4 mb-6">
          {frameworks.map((framework) => (
            <div key={framework.id} className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-sm font-semibold text-slate-900">{framework.framework_name}</h3>
                    <span className={`px-2 py-1 rounded-md font-bold text-[10px] ${getStatusColor(framework.status)}`}>
                      {framework.status}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 mb-4">{framework.company_name}</p>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-slate-500">Compliance Score</p>
                      <p className="text-xl font-bold text-slate-900">{framework.compliance_score}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Requirements</p>
                      <p className="text-sm font-semibold text-slate-900">
                        {framework.completed_requirements}/{framework.total_requirements}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Critical Findings</p>
                      <p className="text-sm font-semibold text-red-600">{framework.critical_findings}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Next Review</p>
                      <p className="text-sm font-medium text-slate-900">{framework.next_review_date || 'Not scheduled'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleStartReview(framework.id)}
                      className="px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Start Review
                    </button>
                    <button
                      onClick={() => handleViewDetails(framework)}
                      className="px-4 py-2.5 border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => handleSubmitForExternalAudit(framework.id)}
                      className="px-4 py-2.5 bg-green-600 text-white font-medium text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={framework.compliance_score < 100}
                      title={framework.compliance_score < 100 ? 'All evidence must be internally verified first' : 'Submit for external audit'}
                    >
                      Submit for External Audit
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {frameworks.length === 0 && (
          <div className="bg-white rounded-3xl border border-slate-100 p-12">
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <FileText size={20} className="text-slate-400" />
            </div>
            <h3 className="text-sm font-semibold text-slate-900 mb-1">No Frameworks to Review</h3>
            <p className="text-sm text-slate-500">There are currently no frameworks assigned for review.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FrameworkReviews;
